import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { apiFetch, put, del } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { keys } from "@/hooks/keys";
import { API_BASE } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SaasConnector {
  id: string;
  connectorId: string;
  connectorSlug: string;
  connectorName: string;
  connectorIcon: string | null;
  scopes: string[];
  createdAt: string;
  updatedAt: string;
}

interface SaasConnectorsListResponse {
  saasConnectors: SaasConnector[];
}

/** One end customer's connection to a registered SaaS connector. */
export interface SaasConnectorConnection {
  id: string;
  name: string;
  connectionScope: string | null;
  status: string;
  verifyStatus: string | null;
  endOrgId: string;
  endOrgName: string | null;
  endOrgSlug: string | null;
  /** Non-archived installations whose pin map references this connection —
   *  disconnecting with pins > 0 breaks those integrations. */
  pinnedInstallations: number;
  createdAt: string;
  updatedAt: string;
}

interface SaasConnectorConnectionsResponse {
  connections: SaasConnectorConnection[];
  pagination?: { total?: number };
}

// ─── Query ────────────────────────────────────────────────────────────────────

export function useSaasConnectors(opts?: { enabled?: boolean }) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.saasConnectors.list(orgId),
    queryFn: async (): Promise<SaasConnector[]> => {
      const data = await apiFetch<SaasConnectorsListResponse>(`${API_BASE}/v1/saas-connectors`);
      return data.saasConnectors ?? [];
    },
    enabled: !!orgId && (opts?.enabled ?? true),
  });
}

/** The end customers' SCOPED connections for one registered SaaS connector,
 *  paginated (server default 20/page). */
export function useSaasConnectorConnections(
  connectorId: string | undefined,
  page: { limit: number; offset: number },
) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.saasConnectors.connections(orgId, connectorId, page.limit, page.offset),
    queryFn: async (): Promise<{ connections: SaasConnectorConnection[]; total: number }> => {
      const qs = new URLSearchParams({ limit: String(page.limit), offset: String(page.offset) });
      const data = await apiFetch<SaasConnectorConnectionsResponse>(
        `${API_BASE}/v1/saas-connectors/${encodeURIComponent(connectorId!)}/connections?${qs.toString()}`,
      );
      return {
        connections: data.connections ?? [],
        total: data.pagination?.total ?? (data.connections ?? []).length,
      };
    },
    enabled: !!orgId && !!connectorId,
  });
}

function invalidateSaasConnectors(orgId?: string) {
  return queryClient.invalidateQueries({ queryKey: keys.saasConnectors.all(orgId) });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function useUpsertSaasConnector() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: ({ connectorId, scopes }: { connectorId: string; scopes: string[] }) =>
      apiFetch<{ id: string; connectorId: string; scopes: string[] }>(
        `${API_BASE}/v1/saas-connectors/${encodeURIComponent(connectorId)}`,
        put({ scopes }),
      ),
    onSuccess: () => invalidateSaasConnectors(orgId),
  });
}

export function useDeleteSaasConnector() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: (connectorId: string) =>
      apiFetch<{ deleted: true }>(
        `${API_BASE}/v1/saas-connectors/${encodeURIComponent(connectorId)}`,
        del,
      ),
    onSuccess: () => invalidateSaasConnectors(orgId),
  });
}

/** Disconnect (delete) one end customer's connection. Goes through the
 *  standard DELETE /api/v1/connections/:id (ownership re-checked server-side);
 *  invalidates both the registry's connections panel and the general
 *  connections caches. */
export function useDisconnectSaasConnection() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: ({ connectionId }: { connectionId: string; connectorId: string }) =>
      apiFetch<{ deleted: true }>(
        `${API_BASE}/v1/connections/${encodeURIComponent(connectionId)}`,
        del,
      ),
    onSuccess: (_data, { connectorId }) => {
      void queryClient.invalidateQueries({ queryKey: keys.saasConnectors.connectionsAll(orgId, connectorId) });
      void queryClient.invalidateQueries({ queryKey: keys.connections.all(orgId) });
    },
  });
}
